import React, { useState, useRef, useEffect } from 'react';
import Navbar from '../components/Navbar';

// ── ATO minimum drawdown ──────────────────────────────────────────────────────
const ATO_RATES = [
  { maxAge: 65, rate: 0.04 }, { maxAge: 75, rate: 0.05 }, { maxAge: 80, rate: 0.06 },
  { maxAge: 85, rate: 0.07 }, { maxAge: 90, rate: 0.09 }, { maxAge: 95, rate: 0.11 },
  { maxAge: Infinity, rate: 0.14 },
];
function atoAnnualRate(age) { return ATO_RATES.find(r => age < r.maxAge)?.rate ?? 0.14; }

// ── Frequency helpers ─────────────────────────────────────────────────────────
const FREQ_OPTS = ['once', 'weekly', 'fortnightly', 'monthly', 'quarterly', 'biannually', 'yearly'];
const FREQ_PER_YEAR = { weekly: 52, fortnightly: 26, monthly: 12, quarterly: 4, biannually: 2, yearly: 1 };
function toMonthly(amount, freq) {
  if (!freq || freq === 'once') return 0;
  return (amount * (FREQ_PER_YEAR[freq] ?? 12)) / 12;
}

// ── Blended monthly rates from allocation rules ───────────────────────────────
// Returns { growthMonthly, incomeMonthly } for the current age.
// Unallocated % falls back to base rates.
function blendedRates(age, allocRules, baseGrowthAnnual, baseIncomeAnnual) {
  const rule = allocRules.find(r => {
    const f = parseFloat(r.fromAge), t = parseFloat(r.toAge);
    return !isNaN(f) && !isNaN(t) && age >= f && age < t;
  });
  if (!rule || !rule.buckets.length) {
    return { growthMonthly: baseGrowthAnnual / 12, incomeMonthly: baseIncomeAnnual / 12 };
  }
  let bG = 0, bI = 0, totalPct = 0;
  for (const b of rule.buckets) {
    const pct = (parseFloat(b.pct) || 0) / 100;
    bG += pct * (parseFloat(b.growth) || 0) / 100;
    bI += pct * (parseFloat(b.income) || 0) / 100;
    totalPct += pct * 100;
  }
  const rem = Math.max(0, 100 - totalPct) / 100;
  return {
    growthMonthly: (bG + rem * baseGrowthAnnual) / 12,
    incomeMonthly: (bI + rem * baseIncomeAnnual) / 12,
  };
}

// ── FIFO parcel helpers ───────────────────────────────────────────────────────
function addParcel(parcels, costBase, monthCreated) {
  if (costBase > 0.001) parcels.push({ costBase, monthCreated, marketValue: costBase });
}

// Sync all parcel market values proportionally to the current balance.
function syncParcelMarketValues(parcels, totalBalance) {
  const totalMV = parcels.reduce((s, p) => s + p.marketValue, 0);
  if (totalMV <= 0) return;
  const ratio = totalBalance / totalMV;
  for (const p of parcels) p.marketValue *= ratio;
}

// Consume `proceeds` FIFO. Returns taxableGain (33.33% CGT discount on parcels held >12m).
function consumeParcels(parcels, proceeds, currentMonth) {
  let remaining = proceeds;
  let taxableGain = 0;
  while (remaining > 0.001 && parcels.length > 0) {
    const p = parcels[0];
    const fraction = Math.min(remaining, p.marketValue) / p.marketValue;
    const costConsumed  = p.costBase    * fraction;
    const valueConsumed = p.marketValue * fraction;
    const gain = Math.max(0, valueConsumed - costConsumed);
    const heldMonths = currentMonth - p.monthCreated;
    taxableGain += heldMonths > 12 ? gain * (2 / 3) : gain;
    if (fraction >= 1 - 1e-9) {
      parcels.shift();
    } else {
      p.costBase    -= costConsumed;
      p.marketValue -= valueConsumed;
    }
    remaining -= valueConsumed;
  }
  return taxableGain;
}

// ── Core calculator ───────────────────────────────────────────────────────────
function calculate({ form, withdrawType, rules, allocRules, enforceAto, superTax, div296 }) {
  const age        = parseFloat(form.current_age)        || 0;
  const retireAge  = parseFloat(form.retirement_age)     || 0;
  const retireYrs  = parseFloat(form.retirement_years)   || 0;
  const baseGrowth = (parseFloat(form.growth_rate)       || 0) / 100;
  const baseIncome = (parseFloat(form.income_rate)       || 0) / 100;
  const initial    = parseFloat(form.initial_investment)  || 0;
  const baseContrib  = parseFloat(form.contribution_amount) || 0;
  const baseWithdraw = parseFloat(form.withdrawal_amount)   || 0;
  const contribFreq  = form.contribution_frequency || 'monthly';
  const withdrawFreq = form.withdrawal_frequency   || 'monthly';

  const baseMonthlyContrib      = toMonthly(baseContrib,  contribFreq);
  const baseMonthlyWithdrawFlat = withdrawType === '$' ? toMonthly(baseWithdraw, withdrawFreq) : 0;
  const withdrawPeriodsPerYear  = FREQ_PER_YEAR[withdrawFreq] ?? 12;

  // Split cashflow events into once-offs and recurring by type
  const onceOffs = rules
    .filter(r => r.freq === 'once')
    .map(r => ({
      age:   parseFloat(r.fromAge),
      delta: r.ruleType === 'contribution'
        ? (parseFloat(r.amount) || 0)
        : -(parseFloat(r.amount) || 0),
    }))
    .filter(o => !isNaN(o.age));

  const recurringContrib  = rules.filter(r => r.ruleType === 'contribution' && r.freq !== 'once');
  const recurringWithdraw = rules.filter(r => r.ruleType === 'withdrawal'   && r.freq !== 'once');

  let balance       = initial;
  let totalContribs = initial;
  const schedule    = [];
  const monthlyData = [];
  const totalMonths = Math.round((retireYrs + (retireAge - age)) * 12);
  let ranOut = false, ranOutAge = 0;

  // FIFO parcel store
  const parcels = [];
  if (initial > 0) addParcel(parcels, initial, 0);

  // FY tracking — boundary every 12 sim months (Option A: assume sim starts 1 July)
  let fyOpeningBalance = initial;
  let fyIncomeEarnings = 0;
  let fyTaxableGains   = 0;

  for (let m = 1; m <= totalMonths; m++) {
    const a         = age + m / 12;
    const isRetired = a >= retireAge;

    let contribThisMonth    = 0;
    let withdrawalThisMonth = 0;
    let growthEarnings      = 0;
    let incomeEarnings      = 0;
    let incomeTaxThisMonth  = 0;
    let div296ThisMonth     = 0;

    // ── Once-off events (fire in the month age first crosses the target) ──
    for (const o of onceOffs) {
      if (a >= o.age && (a - 1 / 12) < o.age) {
        if (o.delta > 0) {
          balance += o.delta;
          totalContribs += o.delta;
          addParcel(parcels, o.delta, m);
          contribThisMonth += o.delta;
        } else {
          const amt = Math.min(-o.delta, balance);
          if (amt > 0) {
            syncParcelMarketValues(parcels, balance);
            fyTaxableGains += consumeParcels(parcels, amt, m);
            balance -= amt;
            withdrawalThisMonth += amt;
          }
        }
      }
    }

    // ── Recurring contributions ──
    // Active cashflow event takes priority over base; base applies during accumulation only.
    const cr = recurringContrib.find(r => {
      const f = parseFloat(r.fromAge), t = parseFloat(r.toAge);
      return !isNaN(f) && !isNaN(t) && a >= f && a < t;
    });
    const mc = cr
      ? toMonthly(parseFloat(cr.amount) || 0, cr.freq)
      : (!isRetired ? baseMonthlyContrib : 0);
    if (mc > 0) {
      balance += mc;
      totalContribs += mc;
      addParcel(parcels, mc, m);
      contribThisMonth += mc;
    }

    // ── Recurring withdrawals ──
    // Active cashflow event takes priority over base; base applies during retirement only.
    const wr = recurringWithdraw.find(r => {
      const f = parseFloat(r.fromAge), t = parseFloat(r.toAge);
      return !isNaN(f) && !isNaN(t) && a >= f && a < t;
    });
    if (wr || isRetired) {
      let mw = 0;
      if (wr) {
        mw = wr.unit === '%'
          ? balance * ((parseFloat(wr.amount) || 0) / 100) / 12
          : toMonthly(parseFloat(wr.amount) || 0, wr.freq);
      } else if (withdrawType === '%') {
        mw = balance * (baseWithdraw / 100) / withdrawPeriodsPerYear;
      } else {
        mw = baseMonthlyWithdrawFlat;
      }
      // ATO floor applies during retirement only
      if (enforceAto && isRetired) mw = Math.max(mw, balance * atoAnnualRate(a) / 12);
      const actual = Math.min(mw, balance);
      if (actual > 0) {
        syncParcelMarketValues(parcels, balance);
        fyTaxableGains += consumeParcels(parcels, actual, m);
        balance -= actual;
        withdrawalThisMonth += actual;
      }
      if (balance <= 0 && !ranOut) { ranOut = true; ranOutAge = Math.round(a * 10) / 10; balance = 0; }
    }

    // ── Monthly return (growth + income) ──
    if (balance > 0) {
      const { growthMonthly, incomeMonthly } = blendedRates(a, allocRules, baseGrowth, baseIncome);
      growthEarnings = balance * growthMonthly;
      const rawIncome = balance * incomeMonthly;

      // Layer 1: 15% tax on income during accumulation phase only
      if (superTax && !isRetired) {
        incomeTaxThisMonth = rawIncome * 0.15;
        incomeEarnings     = rawIncome * 0.85;
      } else {
        incomeEarnings = rawIncome;
      }

      fyIncomeEarnings += incomeEarnings;
      balance += growthEarnings + incomeEarnings;

      // Reinvested net income creates a new parcel at its net cost base
      if (incomeEarnings > 0) addParcel(parcels, incomeEarnings, m);
    }

    // ── Division 296: assessed at FY boundary (every 12 sim months) ──
    if (div296 && m % 12 === 0 && balance > 0) {
      const refTSB          = Math.max(fyOpeningBalance, balance);
      const taxableEarnings = fyIncomeEarnings + fyTaxableGains;

      if (refTSB > 3_000_000 && taxableEarnings > 0) {
        const above3M  = Math.min(refTSB, 10_000_000) - 3_000_000;
        const above10M = Math.max(0, refTSB - 10_000_000);
        let tax = 0;
        if (above3M  > 0) tax += taxableEarnings * (above3M  / refTSB) * 0.15;
        if (above10M > 0) tax += taxableEarnings * (above10M / refTSB) * 0.10;
        div296ThisMonth = Math.max(0, tax);
        syncParcelMarketValues(parcels, balance);
        consumeParcels(parcels, div296ThisMonth, m);
        balance = Math.max(0, balance - div296ThisMonth);
      }
      // Reset FY trackers
      fyOpeningBalance = balance;
      fyIncomeEarnings = 0;
      fyTaxableGains   = 0;
    }

    const netReturn = growthEarnings + incomeEarnings - incomeTaxThisMonth - div296ThisMonth;

    monthlyData.push({
      month: m,
      age:   Math.round(a * 100) / 100,
      balance: Math.max(0, balance),
      contribThisMonth,
      withdrawalThisMonth,
      growthEarnings,
      incomeEarnings,
      incomeTax:  incomeTaxThisMonth,
      div296Tax:  div296ThisMonth,
      netReturn,
      totalContribs,
    });

    if (m % 12 === 0 || m === 1) {
      schedule.push({ age: Math.round(a * 10) / 10, balance: Math.max(0, balance), contributions: totalContribs });
    }
    if (a >= retireAge + retireYrs) break;
  }

  let retireBal = 0;
  for (const e of schedule) { if (Math.abs(e.age - retireAge) < 0.6) { retireBal = e.balance; break; } }

  // Cumulative tax totals
  let cumIncomeTax = 0, cumDiv296 = 0;
  for (const r of monthlyData) { cumIncomeTax += r.incomeTax; cumDiv296 += r.div296Tax; }

  // Yearly rollups
  const yearlyData = [];
  for (let i = 11; i < monthlyData.length; i += 12) {
    const slice = monthlyData.slice(i - 11, i + 1);
    yearlyData.push({
      age:                 monthlyData[i].age,
      balance:             monthlyData[i].balance,
      contribThisMonth:    slice.reduce((s, r) => s + r.contribThisMonth,    0),
      withdrawalThisMonth: slice.reduce((s, r) => s + r.withdrawalThisMonth, 0),
      growthEarnings:      slice.reduce((s, r) => s + r.growthEarnings,      0),
      incomeEarnings:      slice.reduce((s, r) => s + r.incomeEarnings,      0),
      incomeTax:           slice.reduce((s, r) => s + r.incomeTax,           0),
      div296Tax:           slice.reduce((s, r) => s + r.div296Tax,           0),
      netReturn:           slice.reduce((s, r) => s + r.netReturn,           0),
      totalContribs:       monthlyData[i].totalContribs,
    });
  }

  return {
    schedule, monthlyData, yearlyData,
    retireBal, totalContribs, ranOut, ranOutAge,
    finalBalance: schedule.length ? schedule[schedule.length - 1].balance : 0,
    retireAge, cumIncomeTax, cumDiv296,
  };
}

// ── ID helpers ────────────────────────────────────────────────────────────────
let _id = 0;
const uid          = () => ++_id;
const newRule      = () => ({ id: uid(), ruleType: 'contribution', unit: '$', freq: 'monthly', fromAge: '', toAge: '', amount: '' });
const newAllocRule = () => ({ id: uid(), fromAge: '', toAge: '', buckets: [{ id: uid(), pct: '', growth: '', income: '' }] });

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  page:         { minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' },
  content:      { maxWidth: '1200px', margin: '32px auto', padding: '0 20px', width: '100%' },
  title:        { fontSize: '22px', fontWeight: 700, color: 'var(--text)', marginBottom: '24px', fontFamily: "'DM Sans',sans-serif" },
  card:         { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '24px', marginBottom: '24px' },
  grid:         { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' },
  group:        { display: 'flex', flexDirection: 'column', gap: '6px' },
  label:        { fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', fontFamily: "'DM Mono',monospace", letterSpacing: '0.05em' },
  input:        { padding: '8px 12px', fontSize: '14px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'DM Sans',sans-serif", outline: 'none', width: '100%', boxSizing: 'border-box' },
  select:       { padding: '8px 12px', fontSize: '14px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'DM Sans',sans-serif", outline: 'none' },
  btnRow:       { display: 'flex', justifyContent: 'center', marginTop: '20px' },
  btn:          { background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', padding: '10px 32px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" },
  summaryGrid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '24px' },
  summaryItem:  { background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 16px' },
  summaryLabel: { fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', fontFamily: "'DM Mono',monospace", marginBottom: '4px' },
  summaryValue: { fontSize: '18px', fontWeight: 700, color: 'var(--text)' },
  positive:     { color: '#16a34a' },
  warningCol:   { color: '#dc2626' },
  error:        { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--radius)', padding: '12px 16px', color: '#dc2626', fontSize: '13px', marginBottom: '16px' },
  alertInfo:    { background: '#fef9c3', border: '1px solid #fde047', borderRadius: 'var(--radius)', padding: '10px 14px', color: '#854d0e', fontSize: '12px', fontFamily: "'DM Mono',monospace", marginBottom: '8px' },
  chartWrap:    { position: 'relative', height: '380px' },
  chartHeader:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' },
  chartTitle:   { fontSize: '14px', fontWeight: 600, color: 'var(--text)' },
  toggle:       { display: 'flex', gap: '4px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '6px', padding: '3px' },
  toggleBtn:    (active) => ({ padding: '4px 12px', fontSize: '12px', fontWeight: 600, border: 'none', borderRadius: '4px', cursor: 'pointer', fontFamily: "'DM Mono',monospace", background: active ? 'var(--accent)' : 'transparent', color: active ? '#fff' : 'var(--text-muted)' }),
  advHeader:    { background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: '13px', color: 'var(--text)', userSelect: 'none', marginTop: '20px' },
  advContent:   { border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 var(--radius) var(--radius)', padding: '20px', background: 'var(--bg)', display: 'flex', flexDirection: 'column', gap: '20px' },
  advSection:   { display: 'flex', flexDirection: 'column', gap: '8px' },
  advTitle:     { fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', fontFamily: "'DM Mono',monospace", letterSpacing: '0.05em', marginBottom: '2px' },
  advDivider:   { border: 'none', borderTop: '1px solid var(--border)', margin: '4px 0' },
  ruleCard:     { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' },
  ruleRow:      { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
  ruleLabel:    { fontSize: '11px', color: 'var(--text-muted)', fontFamily: "'DM Mono',monospace", whiteSpace: 'nowrap' },
  ruleInput:    { padding: '5px 8px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'DM Mono',monospace", width: '72px' },
  ruleSelect:   { padding: '5px 7px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'DM Mono',monospace" },
  removeBtn:    { padding: '3px 8px', border: 'none', background: '#dc2626', color: 'white', borderRadius: '3px', cursor: 'pointer', fontSize: '11px', fontFamily: "'DM Mono',monospace", flexShrink: 0 },
  addRuleBtn:   { padding: '5px 14px', fontSize: '11px', border: '2px solid var(--accent)', borderRadius: '20px', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontFamily: "'DM Mono',monospace", fontWeight: 600, alignSelf: 'flex-start' },
  addAllocBtn:  { padding: '3px 10px', fontSize: '11px', border: '1px solid var(--border)', borderRadius: '20px', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontFamily: "'DM Mono',monospace", alignSelf: 'flex-start' },
  checkRow:     { display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: 'var(--text)', fontFamily: "'DM Sans',sans-serif", cursor: 'pointer' },
  typeBtn:      (active) => ({ padding: '4px 10px', fontSize: '11px', fontWeight: 600, border: `2px solid ${active ? 'var(--accent)' : 'var(--border)'}`, borderRadius: '20px', cursor: 'pointer', background: active ? 'var(--accent)' : 'transparent', color: active ? '#fff' : 'var(--text-muted)', fontFamily: "'DM Mono',monospace", userSelect: 'none', whiteSpace: 'nowrap' }),
  unitRow:      { display: 'flex', alignItems: 'stretch' },
  unitInput:    { padding: '5px 8px', fontSize: '12px', border: '1px solid var(--border)', borderRight: 'none', borderRadius: 'var(--radius) 0 0 var(--radius)', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'DM Mono',monospace", width: '72px' },
  unitSuffix:   { padding: '5px 8px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '0 var(--radius) var(--radius) 0', background: 'var(--surface)', color: 'var(--text-muted)', fontFamily: "'DM Mono',monospace", whiteSpace: 'nowrap' },
  wdRow:        { display: 'flex', gap: '6px', alignItems: 'center' },
  wdInput:      { padding: '8px 12px', fontSize: '14px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'DM Sans',sans-serif", outline: 'none', flex: 1, minWidth: 0 },
  rateGrid:     { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' },
  tableHeader:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' },
  colToggleRow: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' },
  colBtn:       (active) => ({ padding: '3px 9px', fontSize: '10px', fontWeight: 600, border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`, borderRadius: '20px', cursor: 'pointer', background: active ? 'var(--accent)' : 'transparent', color: active ? '#fff' : 'var(--text-muted)', fontFamily: "'DM Mono',monospace", userSelect: 'none' }),
  tableScroll:  { maxHeight: '400px', overflowY: 'auto', overflowX: 'auto', marginTop: '8px' },
  table:        { width: '100%', borderCollapse: 'collapse', fontSize: '11px', fontFamily: "'DM Mono',monospace" },
  th:           { padding: '6px 10px', background: 'var(--accent)', color: '#fff', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 700, fontSize: '10px', textTransform: 'uppercase', position: 'sticky', top: 0, zIndex: 2 },
  thLeft:       { padding: '6px 10px', background: 'var(--accent)', color: '#fff', textAlign: 'left',  whiteSpace: 'nowrap', fontWeight: 700, fontSize: '10px', textTransform: 'uppercase', position: 'sticky', top: 0, zIndex: 2 },
  td:           { padding: '5px 10px', borderBottom: '1px solid var(--border)', textAlign: 'right', whiteSpace: 'nowrap', color: 'var(--text)' },
  tdLeft:       { padding: '5px 10px', borderBottom: '1px solid var(--border)', textAlign: 'left',  whiteSpace: 'nowrap', color: 'var(--text)' },
  tdNeg:        { padding: '5px 10px', borderBottom: '1px solid var(--border)', textAlign: 'right', whiteSpace: 'nowrap', color: '#dc2626' },
  tdPos:        { padding: '5px 10px', borderBottom: '1px solid var(--border)', textAlign: 'right', whiteSpace: 'nowrap', color: '#16a34a' },
  dsToggleRow:  { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' },
  dsBtn:        (active, color) => ({ padding: '3px 9px', fontSize: '10px', fontWeight: 600, border: `2px solid ${active ? color : 'var(--border)'}`, borderRadius: '20px', cursor: 'pointer', background: active ? color : 'transparent', color: active ? '#fff' : 'var(--text-muted)', fontFamily: "'DM Mono',monospace", userSelect: 'none' }),
};

// ── Formatters ────────────────────────────────────────────────────────────────
function fmtMoney(v) {
  if (v == null || isNaN(v)) return '$0.00';
  return '$' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtMoneyK(v) {
  if (v == null || isNaN(v)) return '$0';
  const a = Math.abs(v);
  if (a >= 1_000_000) return (v < 0 ? '-' : '') + '$' + (a / 1_000_000).toFixed(2) + 'M';
  if (a >= 1_000)     return (v < 0 ? '-' : '') + '$' + (a / 1_000).toFixed(1)     + 'K';
  return '$' + v.toFixed(0);
}

// ── Chart dataset definitions ─────────────────────────────────────────────────
const DS_DEFS = [
  { key: 'balance',      label: 'Balance',             color: '#7c3aed', defaultOn: true  },
  { key: 'contribs',     label: 'Total Contributions', color: '#6b7280', defaultOn: true  },
  { key: 'cumIncomeTax', label: 'Cum. Income Tax',     color: '#f59e0b', defaultOn: false },
  { key: 'cumDiv296',    label: 'Cum. Div296 Tax',     color: '#ef4444', defaultOn: false },
  { key: 'netReturn',    label: 'Net Return (annual)', color: '#10b981', defaultOn: false },
];

// ── Table column definitions ──────────────────────────────────────────────────
const COL_DEFS = [
  { key: 'age',                label: 'Age',            fmt: v => v.toFixed(2), defaultOn: true  },
  { key: 'balance',            label: 'Balance',        fmt: fmtMoney,          defaultOn: true  },
  { key: 'contribThisMonth',   label: 'Contrib',        fmt: fmtMoneyK,         defaultOn: true  },
  { key: 'withdrawalThisMonth',label: 'Withdrawal',     fmt: fmtMoneyK,         defaultOn: true  },
  { key: 'growthEarnings',     label: 'Growth',         fmt: fmtMoneyK,         defaultOn: true  },
  { key: 'incomeEarnings',     label: 'Income',         fmt: fmtMoneyK,         defaultOn: true  },
  { key: 'incomeTax',          label: 'Income Tax',     fmt: fmtMoneyK,         defaultOn: false },
  { key: 'div296Tax',          label: 'Div296 Tax',     fmt: fmtMoneyK,         defaultOn: false },
  { key: 'netReturn',          label: 'Net Return',     fmt: fmtMoneyK,         defaultOn: true  },
  { key: 'totalContribs',      label: 'Total Contribs', fmt: fmtMoney,          defaultOn: false },
];

// ── Component ─────────────────────────────────────────────────────────────────
export default function InvestmentCalculator() {
  const chartRef  = useRef(null);
  const canvasRef = useRef(null);

  const [form, setForm] = useState({
    current_age: '', retirement_age: '', retirement_years: '',
    contribution_amount: '', contribution_frequency: 'monthly',
    growth_rate: '', income_rate: '',
    initial_investment: '',
    withdrawal_amount: '', withdrawal_frequency: 'monthly',
  });
  const [withdrawType,   setWithdrawType]   = useState('$');
  const [showAdv,        setShowAdv]        = useState(false);
  const [enforceAto,     setEnforceAto]     = useState(false);
  const [superTax,       setSuperTax]       = useState(false);
  const [div296,         setDiv296]         = useState(false);
  const [rules,          setRules]          = useState([]);
  const [allocRules,     setAllocRules]     = useState([]);
  const [results,        setResults]        = useState(null);
  const [errors,         setErrors]         = useState([]);
  const [allocWarnings,  setAllocWarnings]  = useState([]);
  const [chartType,      setChartType]      = useState('line');
  const [showTable,      setShowTable]      = useState(false);
  const [tableView,      setTableView]      = useState('yearly');
  const [activeCols,     setActiveCols]     = useState(() => Object.fromEntries(COL_DEFS.map(c => [c.key, c.defaultOn])));
  const [activeDS,       setActiveDS]       = useState(() => Object.fromEntries(DS_DEFS.map(d => [d.key, d.defaultOn])));

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  // Cashflow event CRUD
  const addRule    = ()         => setRules(rs => [...rs, newRule()]);
  const removeRule = id         => setRules(rs => rs.filter(r => r.id !== id));
  const updateRule = (id, f, v) => setRules(rs => rs.map(r => r.id !== id ? r : { ...r, [f]: v }));

  // Allocation rule CRUD
  const addAllocRule    = ()         => setAllocRules(rs => [...rs, newAllocRule()]);
  const removeAllocRule = id         => setAllocRules(rs => rs.filter(r => r.id !== id));
  const updateAllocRule = (id, f, v) => setAllocRules(rs => rs.map(r => r.id !== id ? r : { ...r, [f]: v }));
  const addBucket       = rid        => setAllocRules(rs => rs.map(r => r.id !== rid ? r : { ...r, buckets: [...r.buckets, { id: uid(), pct: '', growth: '', income: '' }] }));
  const removeBucket    = (rid, bid) => setAllocRules(rs => rs.map(r => r.id !== rid ? r : { ...r, buckets: r.buckets.filter(b => b.id !== bid) }));
  const updateBucket    = (rid, bid, f, v) => setAllocRules(rs => rs.map(r => r.id !== rid ? r : { ...r, buckets: r.buckets.map(b => b.id !== bid ? b : { ...b, [f]: v }) }));

  const toggleCol = key => setActiveCols(c => ({ ...c, [key]: !c[key] }));
  const toggleDS  = key => setActiveDS(d  => ({ ...d,  [key]: !d[key]  }));

  const handleSubmit = e => {
    e.preventDefault();
    const errs = [];
    ['current_age', 'retirement_age', 'retirement_years'].forEach(k => {
      if (form[k] === '' || isNaN(parseFloat(form[k]))) errs.push(`${k.replace(/_/g, ' ')} is required.`);
    });
    const warnings = [];
    for (const ar of allocRules) {
      const total = ar.buckets.reduce((s, b) => s + (parseFloat(b.pct) || 0), 0);
      if (total > 100) errs.push(`Allocation rule (age ${ar.fromAge}–${ar.toAge}): ${total}% — must not exceed 100%.`);
      else if (total < 100) warnings.push(`Allocation rule (age ${ar.fromAge}–${ar.toAge}): ${total}% allocated — remaining ${(100 - total).toFixed(1)}% uses base rates.`);
    }
    setAllocWarnings(warnings);
    if (errs.length) { setErrors(errs); return; }
    setErrors([]);
    setResults(calculate({ form, withdrawType, rules, allocRules, enforceAto, superTax, div296 }));
  };

  // ── Chart ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!results || !canvasRef.current) return;
    if (chartRef.current) chartRef.current.destroy();
    const HC = window.Chart;
    if (!HC) return;

    const ages      = results.schedule.map(e => e.age);
    const isBar     = chartType === 'bar';
    const retireIdx = ages.findIndex(a => a >= results.retireAge);

    let ciTax = 0, cdTax = 0;
    const cumIT  = results.yearlyData.map(r => { ciTax += r.incomeTax; return ciTax; });
    const cumD96 = results.yearlyData.map(r => { cdTax += r.div296Tax; return cdTax; });
    const netRet = results.yearlyData.map(r => r.netReturn);

    const allDatasets = [
      { key: 'balance',      label: 'Balance',             data: results.schedule.map(e => e.balance),       borderColor: '#7c3aed', backgroundColor: isBar ? 'rgba(124,58,237,0.7)' : 'rgba(124,58,237,0.1)', fill: !isBar, tension: 0.1 },
      { key: 'contribs',     label: 'Total Contributions', data: results.schedule.map(e => e.contributions),  borderColor: '#6b7280', backgroundColor: isBar ? 'rgba(107,114,128,0.5)' : 'rgba(107,114,128,0.1)', fill: !isBar, tension: 0.1 },
      { key: 'cumIncomeTax', label: 'Cum. Income Tax',     data: cumIT,  borderColor: '#f59e0b', backgroundColor: 'rgba(245,158,11,0.1)',  fill: !isBar, tension: 0.1 },
      { key: 'cumDiv296',    label: 'Cum. Div296 Tax',     data: cumD96, borderColor: '#ef4444', backgroundColor: 'rgba(239,68,68,0.1)',   fill: !isBar, tension: 0.1 },
      { key: 'netReturn',    label: 'Net Return (annual)', data: netRet, borderColor: '#10b981', backgroundColor: 'rgba(16,185,129,0.1)',  fill: !isBar, tension: 0.1, yAxisID: 'y2' },
    ];

    const datasets = allDatasets.filter(d => activeDS[d.key]);
    const hasY2    = activeDS['netReturn'];

    chartRef.current = new HC(canvasRef.current, {
      type: chartType,
      data: { labels: ages, datasets },
      options: {
        responsive: true, maintainAspectRatio: false,
        scales: {
          x:  { title: { display: true, text: 'Age' } },
          y:  { title: { display: true, text: 'Amount ($)' }, ticks: { callback: v => '$' + new Intl.NumberFormat().format(v) } },
          ...(hasY2 ? { y2: { position: 'right', title: { display: true, text: 'Net Return ($)' }, grid: { drawOnChartArea: false }, ticks: { callback: v => '$' + new Intl.NumberFormat().format(v) } } } : {}),
        },
        plugins: {
          tooltip: { callbacks: { label: ctx => ctx.dataset.label + ': $' + new Intl.NumberFormat().format(ctx.raw) } },
          annotation: retireIdx >= 0 ? {
            annotations: { retireLine: { type: 'line', xMin: retireIdx, xMax: retireIdx, borderColor: 'rgba(220,38,38,0.5)', borderWidth: 2, borderDash: [6, 3], label: { display: true, content: 'Retirement', position: 'start', font: { size: 11 } } } }
          } : {}
        }
      }
    });
  }, [results, chartType, activeDS]);

  const tableRows   = tableView === 'monthly' ? results?.monthlyData : results?.yearlyData;
  const visibleCols = COL_DEFS.filter(c => activeCols[c.key]);

  return (
    <div style={S.page}>
      <Navbar />

      <div style={S.content}>
        <h1 style={S.title}>Investment Calculator</h1>

        <div style={S.card}>
          <form onSubmit={handleSubmit}>

            {/* ── Main inputs ── */}
            <div style={S.grid}>
              {[
                { name: 'current_age',        label: 'Current Age',              step: '1'    },
                { name: 'retirement_age',      label: 'Retirement Age',           step: '1'    },
                { name: 'retirement_years',    label: 'Years in Retirement',      step: '1'    },
                { name: 'initial_investment',  label: 'Initial Lump Sum ($)',     step: '1000' },
                { name: 'contribution_amount', label: 'Regular Contribution ($)', step: '10'   },
              ].map(f => (
                <div key={f.name} style={S.group}>
                  <label style={S.label}>{f.label}</label>
                  <input style={S.input} type="number" name={f.name} value={form[f.name]} onChange={handleChange} step={f.step} />
                </div>
              ))}

              {/* Growth + Income rate */}
              <div style={S.group}>
                <label style={S.label}>Annual Return (% / yr)</label>
                <div style={S.rateGrid}>
                  <div style={S.unitRow}>
                    <input style={S.unitInput} type="number" name="growth_rate" placeholder="Growth" value={form.growth_rate} onChange={handleChange} step="0.01" />
                    <span style={S.unitSuffix}>G%</span>
                  </div>
                  <div style={S.unitRow}>
                    <input style={S.unitInput} type="number" name="income_rate" placeholder="Income" value={form.income_rate} onChange={handleChange} step="0.01" />
                    <span style={S.unitSuffix}>I%</span>
                  </div>
                </div>
              </div>

              {/* Withdrawal with $/% toggle */}
              <div style={S.group}>
                <label style={S.label}>{withdrawType === '%' ? 'Withdrawal Rate (% / yr)' : 'Retirement Withdrawal ($)'}</label>
                <div style={S.wdRow}>
                  <input style={S.wdInput} type="number" name="withdrawal_amount" value={form.withdrawal_amount} onChange={handleChange} step={withdrawType === '%' ? '0.1' : '100'} />
                  <button type="button" style={S.typeBtn(withdrawType === '$')} onClick={() => setWithdrawType('$')}>$</button>
                  <button type="button" style={S.typeBtn(withdrawType === '%')} onClick={() => setWithdrawType('%')}>%</button>
                </div>
              </div>

              {/* Frequency selects */}
              {[
                { name: 'contribution_frequency', label: 'Contribution Frequency', opts: ['weekly','fortnightly','monthly','quarterly','biannually','yearly'] },
                { name: 'withdrawal_frequency',   label: 'Withdrawal Frequency',   opts: ['weekly','fortnightly','monthly','quarterly','biannually','yearly'] },
              ].map(f => (
                <div key={f.name} style={S.group}>
                  <label style={S.label}>{f.label}</label>
                  <select style={S.select} name={f.name} value={form[f.name]} onChange={handleChange}>
                    {f.opts.map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>)}
                  </select>
                </div>
              ))}
            </div>

            {/* ── Advanced settings (inside card) ── */}
            <div style={S.advHeader} onClick={() => setShowAdv(v => !v)}>
              <span>Advanced Settings</span>
              <span style={{ fontSize: '14px', color: 'var(--text-muted)' }}>{showAdv ? '▲' : '▼'}</span>
            </div>

            {showAdv && (
              <div style={S.advContent}>

                {/* Super Tax */}
                <div style={S.advSection}>
                  <div style={S.advTitle}>Super Tax Settings</div>
                  <label style={S.checkRow}>
                    <input type="checkbox" checked={superTax} onChange={e => setSuperTax(e.target.checked)} style={{ width: '15px', height: '15px', cursor: 'pointer' }} />
                    Apply 15% tax on income earnings during accumulation
                  </label>
                  {superTax && (
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: "'DM Mono',monospace", paddingLeft: '25px' }}>
                      Income (I%) taxed at 15% each period during accumulation. Retirement phase tax-free.
                    </div>
                  )}
                  <hr style={S.advDivider} />
                  <label style={S.checkRow}>
                    <input type="checkbox" checked={div296} onChange={e => setDiv296(e.target.checked)} style={{ width: '15px', height: '15px', cursor: 'pointer' }} />
                    Apply Division 296 tax (balances above $3M)
                  </label>
                  {div296 && (
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: "'DM Mono',monospace", paddingLeft: '25px', lineHeight: 1.8 }}>
                      Additional 15% on earnings attributable to balance $3M–$10M. Extra 10% above $10M. FIFO parcel tracking with 33.33% CGT discount on parcels held &gt;12 months. Assessed annually.
                    </div>
                  )}
                </div>

                <hr style={S.advDivider} />

                {/* ATO minimum drawdown */}
                <div style={S.advSection}>
                  <div style={S.advTitle}>Super Minimum Drawdown (ATO)</div>
                  <label style={S.checkRow}>
                    <input type="checkbox" checked={enforceAto} onChange={e => setEnforceAto(e.target.checked)} style={{ width: '15px', height: '15px', cursor: 'pointer' }} />
                    Enforce ATO minimum drawdown based on age
                  </label>
                  {enforceAto && (
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: "'DM Mono',monospace", lineHeight: 1.8 }}>
                      &lt;65 → 4% · 65–74 → 5% · 75–79 → 6% · 80–84 → 7% · 85–89 → 9% · 90–94 → 11% · 95+ → 14%
                    </div>
                  )}
                </div>

                <hr style={S.advDivider} />

                {/* Cashflow Events */}
                <div style={S.advSection}>
                  <div style={S.advTitle}>Cashflow Events</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: "'DM Mono',monospace", marginBottom: '4px' }}>
                    Override contributions or withdrawals for an age range, or add a once-off lump sum event.
                  </div>

                  {rules.map(rule => (
                    <div key={rule.id} style={S.ruleCard}>
                      <div style={S.ruleRow}>
                        <button type="button" style={S.typeBtn(rule.ruleType === 'contribution')} onClick={() => updateRule(rule.id, 'ruleType', 'contribution')}>CONTRIB</button>
                        <button type="button" style={S.typeBtn(rule.ruleType === 'withdrawal')}   onClick={() => updateRule(rule.id, 'ruleType', 'withdrawal')}>WITHDRAW</button>
                        {rule.ruleType === 'withdrawal' && (
                          <>
                            <button type="button" style={S.typeBtn(rule.unit === '$')} onClick={() => updateRule(rule.id, 'unit', '$')}>$</button>
                            <button type="button" style={S.typeBtn(rule.unit === '%')} onClick={() => updateRule(rule.id, 'unit', '%')}>%</button>
                          </>
                        )}
                        <select style={S.ruleSelect} value={rule.freq} onChange={e => updateRule(rule.id, 'freq', e.target.value)}>
                          {FREQ_OPTS.map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>)}
                        </select>
                        <button style={{ ...S.removeBtn, marginLeft: 'auto' }} type="button" onClick={() => removeRule(rule.id)}>✕</button>
                      </div>
                      <div style={S.ruleRow}>
                        {rule.freq === 'once' ? (
                          <>
                            <span style={S.ruleLabel}>At age</span>
                            <input style={S.ruleInput} type="number" placeholder="Age" value={rule.fromAge} onChange={e => updateRule(rule.id, 'fromAge', e.target.value)} />
                          </>
                        ) : (
                          <>
                            <span style={S.ruleLabel}>Age</span>
                            <input style={S.ruleInput} type="number" placeholder="From" value={rule.fromAge} onChange={e => updateRule(rule.id, 'fromAge', e.target.value)} />
                            <span style={S.ruleLabel}>to</span>
                            <input style={S.ruleInput} type="number" placeholder="To"   value={rule.toAge}   onChange={e => updateRule(rule.id, 'toAge',   e.target.value)} />
                          </>
                        )}
                        <span style={S.ruleLabel}>Amount</span>
                        <div style={S.unitRow}>
                          <input style={S.unitInput} type="number" placeholder="0" value={rule.amount} onChange={e => updateRule(rule.id, 'amount', e.target.value)} />
                          <span style={S.unitSuffix}>{rule.ruleType === 'withdrawal' && rule.unit === '%' ? '% / yr' : '$'}</span>
                        </div>
                      </div>
                    </div>
                  ))}

                  <button style={S.addRuleBtn} type="button" onClick={addRule}>+ Add Event</button>
                </div>

                <hr style={S.advDivider} />

                {/* Portfolio Allocation Rules */}
                <div style={S.advSection}>
                  <div style={S.advTitle}>Portfolio Allocation Rules</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: "'DM Mono',monospace", marginBottom: '4px' }}>
                    Override growth and income rates by age range. Unallocated % uses the base rates from the main form.
                  </div>

                  {allocRules.map(ar => {
                    const totalPct = ar.buckets.reduce((s, b) => s + (parseFloat(b.pct) || 0), 0);
                    const over = totalPct > 100;
                    return (
                      <div key={ar.id} style={{ ...S.ruleCard, border: `1px solid ${over ? '#fecaca' : 'var(--border)'}` }}>
                        <div style={S.ruleRow}>
                          <span style={S.ruleLabel}>Age</span>
                          <input style={S.ruleInput} type="number" placeholder="From" value={ar.fromAge} onChange={e => updateAllocRule(ar.id, 'fromAge', e.target.value)} />
                          <span style={S.ruleLabel}>to</span>
                          <input style={S.ruleInput} type="number" placeholder="To"   value={ar.toAge}   onChange={e => updateAllocRule(ar.id, 'toAge',   e.target.value)} />
                          <span style={{ fontSize: '11px', fontWeight: 700, fontFamily: "'DM Mono',monospace", color: over ? '#dc2626' : totalPct < 100 ? '#854d0e' : '#16a34a', marginLeft: 'auto' }}>
                            {totalPct}% allocated
                          </span>
                          <button style={S.removeBtn} type="button" onClick={() => removeAllocRule(ar.id)}>✕</button>
                        </div>
                        {ar.buckets.map(b => (
                          <div key={b.id} style={S.ruleRow}>
                            <div style={S.unitRow}>
                              <input style={S.unitInput} type="number" placeholder="0"  value={b.pct}    onChange={e => updateBucket(ar.id, b.id, 'pct',    e.target.value)} />
                              <span style={S.unitSuffix}>%</span>
                            </div>
                            <span style={S.ruleLabel}>of portfolio:</span>
                            <div style={S.unitRow}>
                              <input style={S.unitInput} type="number" placeholder="G%" value={b.growth} onChange={e => updateBucket(ar.id, b.id, 'growth', e.target.value)} />
                              <span style={S.unitSuffix}>G%</span>
                            </div>
                            <div style={S.unitRow}>
                              <input style={S.unitInput} type="number" placeholder="I%" value={b.income} onChange={e => updateBucket(ar.id, b.id, 'income', e.target.value)} />
                              <span style={S.unitSuffix}>I%</span>
                            </div>
                            {ar.buckets.length > 1 && (
                              <button style={{ ...S.removeBtn, background: 'var(--border)', color: 'var(--text-muted)' }} type="button" onClick={() => removeBucket(ar.id, b.id)}>✕</button>
                            )}
                          </div>
                        ))}
                        <button style={S.addAllocBtn} type="button" onClick={() => addBucket(ar.id)}>+ Add Allocation</button>
                      </div>
                    );
                  })}

                  <button style={S.addRuleBtn} type="button" onClick={addAllocRule}>+ Add Allocation Rule</button>
                </div>

              </div>
            )}

            <div style={S.btnRow}>
              <button type="submit" style={S.btn}>Calculate</button>
            </div>
          </form>
        </div>

        {errors.length > 0 && <div style={S.error}>{errors.map((e, i) => <div key={i}>{e}</div>)}</div>}
        {allocWarnings.map((w, i) => <div key={i} style={S.alertInfo}>{w}</div>)}

        {results && (
          <>
            {/* ── Summary + Chart ── */}
            <div style={S.card}>
              <div style={S.summaryGrid}>
                <div style={S.summaryItem}>
                  <div style={S.summaryLabel}>Balance at Retirement</div>
                  <div style={S.summaryValue}>{fmtMoney(results.retireBal)}</div>
                </div>
                <div style={S.summaryItem}>
                  <div style={S.summaryLabel}>Total Contributions</div>
                  <div style={S.summaryValue}>{fmtMoney(results.totalContribs)}</div>
                </div>
                <div style={S.summaryItem}>
                  <div style={S.summaryLabel}>Investment Return</div>
                  <div style={{ ...S.summaryValue, ...S.positive }}>{fmtMoney(results.retireBal - results.totalContribs)}</div>
                </div>
                {superTax && (
                  <div style={S.summaryItem}>
                    <div style={S.summaryLabel}>Total Income Tax</div>
                    <div style={{ ...S.summaryValue, color: '#f59e0b' }}>{fmtMoney(results.cumIncomeTax)}</div>
                  </div>
                )}
                {div296 && (
                  <div style={S.summaryItem}>
                    <div style={S.summaryLabel}>Total Div296 Tax</div>
                    <div style={{ ...S.summaryValue, color: '#ef4444' }}>{fmtMoney(results.cumDiv296)}</div>
                  </div>
                )}
                {results.ranOut
                  ? <div style={S.summaryItem}><div style={S.summaryLabel}>Funds Depleted At</div><div style={{ ...S.summaryValue, ...S.warningCol }}>Age {results.ranOutAge}</div></div>
                  : <div style={S.summaryItem}><div style={S.summaryLabel}>End of Retirement Balance</div><div style={{ ...S.summaryValue, ...S.positive }}>{fmtMoney(results.finalBalance)}</div></div>
                }
              </div>

              {/* Dataset toggles */}
              <div style={S.dsToggleRow}>
                {DS_DEFS.map(d => (
                  <button key={d.key} type="button" style={S.dsBtn(activeDS[d.key], d.color)} onClick={() => toggleDS(d.key)}>{d.label}</button>
                ))}
              </div>

              <div style={S.chartHeader}>
                <span style={S.chartTitle}>Investment Growth Over Time</span>
                <div style={S.toggle}>
                  <button style={S.toggleBtn(chartType === 'line')} onClick={() => setChartType('line')}>LINE</button>
                  <button style={S.toggleBtn(chartType === 'bar')}  onClick={() => setChartType('bar')}>BAR</button>
                </div>
              </div>
              <div style={S.chartWrap}><canvas ref={canvasRef} /></div>
            </div>

            {/* ── Detailed Schedule Table ── */}
            <div style={S.card}>
              <div style={S.tableHeader}>
                <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>Detailed Schedule</span>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <div style={S.toggle}>
                    <button style={S.toggleBtn(tableView === 'yearly')}  onClick={() => setTableView('yearly')}>YEARLY</button>
                    <button style={S.toggleBtn(tableView === 'monthly')} onClick={() => setTableView('monthly')}>MONTHLY</button>
                  </div>
                  <button style={S.toggleBtn(showTable)} onClick={() => setShowTable(v => !v)}>
                    {showTable ? 'HIDE' : 'SHOW'}
                  </button>
                </div>
              </div>

              {showTable && (
                <>
                  <div style={S.colToggleRow}>
                    {COL_DEFS.map(c => (
                      <button key={c.key} type="button" style={S.colBtn(activeCols[c.key])} onClick={() => toggleCol(c.key)}>{c.label}</button>
                    ))}
                  </div>
                  <div style={S.tableScroll}>
                    <table style={S.table}>
                      <thead>
                        <tr>
                          {visibleCols.map(c => (
                            <th key={c.key} style={c.key === 'age' ? S.thLeft : S.th}>{c.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tableRows?.map((row, i) => (
                          <tr key={i} style={{ background: i % 2 === 0 ? 'var(--surface)' : 'var(--bg)' }}>
                            {visibleCols.map(c => {
                              const v = row[c.key];
                              const isNeg = ['withdrawalThisMonth', 'incomeTax', 'div296Tax'].includes(c.key) && v > 0;
                              const isPos = ['growthEarnings', 'incomeEarnings', 'netReturn'].includes(c.key) && v > 0;
                              const style = isNeg ? S.tdNeg : isPos ? S.tdPos : c.key === 'age' ? S.tdLeft : S.td;
                              return <td key={c.key} style={style}>{c.fmt(v)}</td>;
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
